import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatDistanceToNow } from "date-fns";
import { sv } from "date-fns/locale";
import SportIcon from "@/components/SportIcon";
import { cn } from "@/lib/utils";
import { SPORT_OPTIONS } from "@/lib/sports";

interface Props {
  org: Record<string, unknown>;
  updateField: (field: string, value: unknown) => void;
}

const SOURCE_LABEL_KEYS: Record<string, string> = {
  web_admin: "settings.general.sourceWebAdmin",
  web_player: "settings.general.sourceWebPlayer",
  tv_display: "settings.general.sourceTvDisplay",
  pos_system: "settings.general.sourcePosSystem",
};

export default function SettingsGeneral({ org, updateField }: Props) {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();

  const { data: heartbeats = [] } = useQuery({
    queryKey: ["system-heartbeats", organization?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("system_heartbeats")
        .select("*")
        .eq("organization_id", organization!.id);
      return data ?? [];
    },
    enabled: !!organization?.id,
  });

  return (
    <div className="space-y-6">
      {/* Sport type selector */}
      <div>
        <h3 className="text-lg font-semibold mb-1">{t("settings.general.sportTypeTitle")}</h3>
        <p className="text-sm text-muted-foreground mb-3">{t("settings.general.sportTypeDescription")}</p>
        <div className="grid grid-cols-3 gap-2">
          {SPORT_OPTIONS.map((s) => {
            const selected = (org.sport_type || "padel") === s.value;
            return (
              <button
                key={s.value}
                onClick={() => updateField("sport_type", s.value)}
                className={cn(
                  "flex flex-col items-center gap-1.5 rounded-lg border p-3 text-sm transition-colors",
                  selected
                    ? "border-primary bg-primary/5 font-medium"
                    : "hover:border-primary/50"
                )}
              >
                <SportIcon sport={s.value} size="md" />
                <span className="text-xs">{s.value === "generic" ? t("settings.general.sportOther") : s.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="border-t pt-4">
        <h3 className="text-lg font-semibold mb-1">{t("settings.general.otherSettingsTitle")}</h3>
        <p className="text-sm text-muted-foreground mb-4">{t("settings.general.otherSettingsDescription")}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>{t("settings.general.currency")}</Label>
          <Select value={org.default_currency || "SEK"} onValueChange={(v) => updateField("default_currency", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="SEK">SEK (kr)</SelectItem>
              <SelectItem value="NOK">NOK (kr)</SelectItem>
              <SelectItem value="DKK">DKK (kr)</SelectItem>
              <SelectItem value="EUR">EUR (€)</SelectItem>
              <SelectItem value="USD">USD ($)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>{t("settings.general.timezone")}</Label>
          <Select value={org.timezone || "Europe/Stockholm"} onValueChange={(v) => updateField("timezone", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Europe/Stockholm">{t("settings.general.tzStockholm")}</SelectItem>
              <SelectItem value="Europe/Oslo">{t("settings.general.tzOslo")}</SelectItem>
              <SelectItem value="Europe/Copenhagen">{t("settings.general.tzCopenhagen")}</SelectItem>
              <SelectItem value="Europe/Helsinki">{t("settings.general.tzHelsinki")}</SelectItem>
              <SelectItem value="Europe/London">{t("settings.general.tzLondon")}</SelectItem>
              <SelectItem value="Europe/Berlin">{t("settings.general.tzBerlin")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>{t("settings.general.defaultLanguage")}</Label>
          <Select value={org.default_language || "sv"} onValueChange={(v) => updateField("default_language", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="sv">{t("settings.general.langSwedish")}</SelectItem>
              <SelectItem value="no">{t("settings.general.langNorwegian")}</SelectItem>
              <SelectItem value="da">{t("settings.general.langDanish")}</SelectItem>
              <SelectItem value="en">{t("settings.general.langEnglish")}</SelectItem>
              <SelectItem value="fi">{t("settings.general.langFinnish")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Uptime monitoring */}
      <div className="space-y-4 pt-4 border-t">
        <div>
          <h3 className="text-lg font-semibold mb-1">{t("settings.general.uptimeTitle")}</h3>
          <p className="text-sm text-muted-foreground">{t("settings.general.uptimeDescription")}</p>
        </div>

        <div className="flex items-center justify-between rounded-lg border p-4">
          <div>
            <Label>{t("settings.general.uptimeAlert")}</Label>
            <p className="text-xs text-muted-foreground">{t("settings.general.uptimeAlertHint")}</p>
          </div>
          <Switch
            checked={org.uptime_alert_enabled ?? false}
            onCheckedChange={(v) => updateField("uptime_alert_enabled", v)}
          />
        </div>

        {org.uptime_alert_enabled && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>{t("settings.general.alertAfterMinutes")}</Label>
              <NumberInput
                min={5}
                value={org.uptime_alert_minutes ?? 15}
                onValueChange={(v) => updateField("uptime_alert_minutes", v || 15)}
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label>{t("settings.general.sendTo")}</Label>
              <Input
                type="email"
                value={org.uptime_alert_email ?? ""}
                onChange={(e) => updateField("uptime_alert_email", e.target.value)}
                placeholder={org.contact_email ?? "admin@example.com"}
                className="h-8 text-sm"
              />
            </div>
          </div>
        )}

        {heartbeats.length > 0 && (
          <div className="space-y-2">
            <Label className="text-sm">{t("settings.general.currentStatus")}</Label>
            <div className="space-y-1.5">
              {heartbeats.map((hb: any) => {
                const minutesAgo = (Date.now() - new Date(hb.last_heartbeat).getTime()) / 60000;
                const isOk = minutesAgo < (org.uptime_alert_minutes ?? 15);
                return (
                  <div key={hb.id} className="flex items-center justify-between rounded border p-2 text-sm">
                    <span>{SOURCE_LABEL_KEYS[hb.source] ? t(SOURCE_LABEL_KEYS[hb.source]) : hb.source}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(hb.last_heartbeat), { locale: sv, addSuffix: true })}
                      </span>
                      <Badge variant={isOk ? "outline" : "destructive"} className="text-[10px]">
                        {isOk ? "OK" : "Offline"}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
